/*
* Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
*/
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import {NotificationPanelTabs} from '../../types/types';

// defining props definition for Tab Badge View component...
export interface TabBadgeViewProps {
  urgentNotifications?: any;
  allNotifications?: any;
  tab?: NotificationPanelTabs;
};

// Figma "Tabs/Selected/MWeb": both badges are 18x18 ...
const getSharedBadgeStyles = (size = 18) => ({
  width          : `${size}px`,
  height         : `${size}px`,
  // without this, the 2px top/bottom padding is added on top of the 18px
  // height (content-box default) while width stays 18px - an 18x22 box
  // with borderRadius:100px renders as an oval, not the circle Figma
  // wants, since border-box keeps padding/border inside the 18x18 box ...
  boxSizing      : 'border-box',
  display        : 'flex',
  alignItems     : 'center',
  flexShrink     : 0,
  paddingTop     : '2px',
  borderRadius   : '100px',
  paddingBottom  : '2px',
  justifyContent : 'center',
  border         : `1px solid ${dlsColor('white')}`
} as const);

const sharedBadgeTextStyles = {
  fontSize      : '10px',
  textAlign     : 'center',
  fontFamily    : 'Inter',
  fontWeight    : 600,
  lineHeight    : '12px',
  letterSpacing : '0px'
} as const;

// defining the style definition for Tab Badge View component...
const useStyles = makeStyles(() => {
  return {
    urgentBadge : {
      ...getSharedBadgeStyles(),
      backgroundColor : dlsColor('red')
    },
    activityBadge : {
      ...getSharedBadgeStyles(),
      backgroundColor : dlsColor('grey5')
    },
    urgentBadgeText : {
      ...sharedBadgeTextStyles,
      color : dlsColor('white')
    },
    activityBadgeText : {
      ...sharedBadgeTextStyles,
      color : dlsColor('grey1')
    }
  };
});

/**
 * Defines the tab badge view component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *  import {TabBadgeView} from '@ion/dls/src/components/tabs';
 *
 *  <TabBadgeView tab={tab} onClose={onClose} index={index} />
 *
 * ```
 */
export const TabBadgeView = ({
  tab,
  allNotifications,
  urgentNotifications
}: TabBadgeViewProps) => {
  const classes = useStyles();

  return (
    <Render>
      <Render.If condition={urgentNotifications?.length && tab === NotificationPanelTabs.urgent}>
        <div className={classes.urgentBadge}>
          <span className={classes.urgentBadgeText}>
            {urgentNotifications?.length}
          </span>
        </div>
      </Render.If>
      <Render.If condition={allNotifications?.length && tab === NotificationPanelTabs.allActivity}>
        <div className={classes.activityBadge}>
          <span className={classes.activityBadgeText}>
            {allNotifications?.length}
          </span>
        </div>
      </Render.If>
    </Render>
  );
};

export default memo(TabBadgeView);
